// Connect paths (docs/specs/CONNECT.md 6.5): a path's blocks drawn left to right, used on Conversations › Routes, the firm and
// deal pages and the path_step card. Each step says who does it now: Ogma, Ogma watching for it, or you (with the
// phase that automates it). On a firm's run each step is done, current or still to do.
import { Link } from "react-router-dom";
import { blockWho, type BlockKey, type BoardBlock, type ConnectRun } from "../lib/paths";

type Step = { key: BlockKey; label: string; kind: "watched" | "person"; automated_in?: string | null; off?: boolean; firms?: number; late?: number; skipped?: boolean; state?: "done" | "current" | "to_do"; href?: string };

/** `onOpen`: each step is a button that opens its settings (Conversations › Routes; a click or a double
 * click). */
export function PathSteps({ steps, label, showWho = true, onOpen }: { steps: Step[]; label: string; showWho?: boolean; onOpen?: (key: BlockKey) => void }) {
  return (
    <ol className="path-steps" aria-label={label}>
      {steps.map((s, i) => {
        const who = blockWho(s);
        const cls = ["path-step", s.off ? "is-off" : "", s.state ? `is-${s.state.replace("_", "-")}` : "", `who-${who.tone}`].filter(Boolean).join(" ");
        const body = (
          <>
            <span className="path-step-n" aria-hidden="true">
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className="path-step-label">{s.label}</span>
            {s.off ? (
              <span className="path-step-who">Switched off</span>
            ) : (
              showWho && (
                <span className="path-step-who" title={who.note}>
                  {who.label}
                  {s.kind === "person" && s.automated_in ? ` · Ogma from ${s.automated_in}` : ""}
                </span>
              )
            )}
            {typeof s.firms === "number" && !s.off && <span className="path-step-firms">{s.firms === 1 ? "1 firm" : `${s.firms} firms`}</span>}
            {s.late ? <span className="path-step-late">{s.late} late</span> : null}
            {s.skipped ? <span className="path-step-who">Skipped</span> : null}
            {s.state === "current" && <span className="sr-only"> (current step)</span>}
            {s.state === "done" && <span className="sr-only"> (done)</span>}
          </>
        );
        return (
          <li key={s.key} className={cls}>
            {s.href ? (
              <Link to={s.href} className="path-step-link">
                {body}
              </Link>
            ) : onOpen && !s.off ? (
              <button type="button" className="path-step-link path-step-open" aria-label={`Set up ${s.label}`} onClick={() => onOpen(s.key)}>
                {body}
              </button>
            ) : (
              body
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** A board path's blocks with the firms at each, and how many of them are past the step's pace. */
export function boardSteps(blocks: BoardBlock[], late: Partial<Record<BlockKey, number>> = {}): Step[] {
  return blocks.map((b) => ({ key: b.key, label: b.label, kind: b.kind, automated_in: b.automated_in ?? null, off: b.off, firms: b.firms, late: late[b.key] }));
}

/** A firm's run: each block done, current, to do or skipped by a route. */
export function runSteps(run: Pick<ConnectRun, "blocks" | "skipped">, automated: (k: BlockKey) => string | null): Step[] {
  return run.blocks.map((b) => ({ key: b.key, label: b.label, kind: b.kind, automated_in: automated(b.key), state: b.state, skipped: run.skipped?.includes(b.key) }));
}
